import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HubHttpPaginationResponse } from '@core/interfaces/HubResponse';
import {
  CategoryTypeOfSearch,
  ResultOfMainSearch,
  SearchResultCountStats,
} from '@core/interfaces/main-search';
import { environment } from '@env';
import { Observable } from 'rxjs';
import { HubCatchHttpErrorService } from '@core/services/hub-catch-http-error/hub-catch-http-error.service';

export interface RecentSearch {
  text: string;
  category: string;
  bookmarked?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MainSearchService {
  private http = inject(HttpClient);

  recentSearches: RecentSearch[];

  private readonly recentSearchesKey = 'hub.recent-search';

  private readonly recentSearchesLimit = 10;

  private readonly hubCatchHttpErrorService = inject(HubCatchHttpErrorService);

  constructor() {
    try {
      this.recentSearches = JSON.parse(localStorage.getItem(this.recentSearchesKey) || '[]');
    } catch {
      this.recentSearches = [];
    }
  }

  search(data: {
    text: string;
    type?: CategoryTypeOfSearch;
    bookmarked?: boolean;
    offset?: number;
    limit?: number;
    sort?: string;
    resourceId?: string;
  }): Observable<HubHttpPaginationResponse<ResultOfMainSearch>> {
    // TODO: need to refactor the method signature, the query params should be dynamic.

    const { text, ...queryParams } = data;

    let params = new HttpParams();
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });

    const url = `${environment.BACKEND_DOMAIN}/api/v1/search`;
    return this.http
      .post<HubHttpPaginationResponse<ResultOfMainSearch>>(url, { text }, { params })
      .pipe(this.hubCatchHttpErrorService.catchHttpError());
  }

  clearRecentSearch(): void {
    localStorage.removeItem(this.recentSearchesKey);
    this.recentSearches = [];
  }

  addRecentSearch(params: RecentSearch): void {
    const sameEntryIndex = this.findSameEntryIndex(params);
    this.recentSearches.unshift(params);
    if (sameEntryIndex > -1) {
      this.recentSearches.splice(sameEntryIndex + 1, 1);
    } else {
      this.recentSearches.splice(this.recentSearchesLimit);
    }

    localStorage.setItem(this.recentSearchesKey, JSON.stringify(this.recentSearches));
  }

  getRecentSearches(): RecentSearch[] {
    return this.recentSearches;
  }

  searchResultsCount(
    text: string,
    bookmarked: boolean | undefined,
  ): Observable<SearchResultCountStats> {
    const url = `${environment.BACKEND_DOMAIN}/api/v1/search/count${
      bookmarked ? `?bookmarked=${bookmarked}` : ''
    } `;
    return this.http.post<SearchResultCountStats>(url, { text });
  }

  private findSameEntryIndex({ text, category, bookmarked }: RecentSearch): number {
    return this.recentSearches.findIndex(
      (search) =>
        search.text?.toLowerCase() === text?.toLowerCase() &&
        search.category?.toLowerCase() === category?.toLowerCase() &&
        search.bookmarked === bookmarked,
    );
  }
}
